import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    // Explicit muted/playsInline keeps autoplay policy satisfied after programmatic src + load().
    videoElement.muted = true;
    videoElement.playsInline = true;

    videoElement.load();

    const tryPlay = () => {
      const result = videoElement.play();
      if (result instanceof Promise) {
        result.catch(() => {
          // Autoplay blocked or unsupported; background video stays on poster.
        });
      }
    };

    // load() resets the element; the autoplay attribute is not reliably reapplied — call play()
    // once enough data exists (especially when the first source is HLS and the browser falls back).
    if (videoElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      tryPlay();
    } else {
      videoElement.addEventListener('canplay', tryPlay, { once: true });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
